import {
  onCleanup,
  createEffect,
  createRoot,
  createMemo,
  on,
  createSignal,
} from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/+esm';
import { insert } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/web/+esm';
import { register, compose } from 'https://cdn.jsdelivr.net/npm/component-register@0.6.7/+esm';

// Utility custom element functions

function createProps(raw) {
  const keys = Object.keys(raw);
  const props = {};
  for (let i = 0; i < keys.length; i++) {
    const [get, set] = createSignal(raw[keys[i]]);
    Object.defineProperty(props, keys[i], {
      get() {
        return get();
      },
      set(v) {
        set(() => v);
      },
    });
  }
  return props;
}

function lookupContext(el) {
  if (el.assignedSlot && el.assignedSlot._$owner) return el.assignedSlot._$owner;
  let next = el.parentNode;
  while (next && !next._$owner && !(next.assignedSlot && next.assignedSlot._$owner)) next = next.parentNode;
  return next && next.assignedSlot ? next.assignedSlot._$owner : el._$owner;
}

function withSolid(ComponentType) {
  return (rawProps, options) => {
    const { element } = options;
    return createRoot((dispose) => {
      const props = createProps(rawProps);
      element.addPropertyChangedCallback((key, val) => (props[key] = val));
      element.addReleaseCallback(() => {
        element.renderRoot.textContent = '';
        dispose();
      });
      const comp = ComponentType(props, options);
      return insert(element.renderRoot, comp);
    }, lookupContext(element));
  };
}

function withNoShadowDOM(ComponentType = (props, options) => {}) {
  return (props, options) => {
    const { element } = options;
    element.renderRoot = element;
    return ComponentType(props, options);
  };
}

const customShadowlessElement = (tagName, props = {}, Component = (props, options) => {}, ...rest) =>
  compose(register(tagName, props), withNoShadowDOM, withSolid, ...rest)(Component);

function handleRadioChange(form, input) {
  const element = Array.from(form.elements).find(
    (elem) => elem.name === input.name && elem.value === input.value && !elem.checked
  );
  if (!element) return;
  element.checked = true;
  element.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

function handleDefaultChange(form, input) {
  const element = Array.from(form.elements).find((elem) => elem.name === input.name && elem.value !== input.value);
  if (!element) return;
  element.value = input.value;
  element.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

function receiveUpdate(form) {
  return function (event) {
    if (event.detail.relatedTarget === form) return;
    const input = event.detail.changedTarget;

    switch (input.type) {
      case 'radio':
        return handleRadioChange(form, input);
      default:
        return handleDefaultChange(form, input);
    }
  };
}

function FormSync(props, { element }) {
  if (!props.name && !props.formId) return;
  const form = createMemo(() => element.querySelector(`#${props.formId}`));
  const UPDATE = createMemo(() => `form.update.${props.name}`);

  const handleUpdate = (form) => (event) => {
    dispatchEvent(
      new CustomEvent(UPDATE(), {
        detail: {
          relatedTarget: form,
          changedTarget: event.target,
        },
      })
    );
  };

  createEffect(
    on(form, (form) => {
      if (!form || !props.name) return;
      element.addEventListener('change', handleUpdate(form));
      addEventListener(UPDATE(), receiveUpdate(form));

      onCleanup(() => {
        element.removeEventListener('change', handleUpdate(form));
        removeEventListener(UPDATE(), receiveUpdate(form));
      });
    })
  );
}

customShadowlessElement(
  'form-sync',
  {
    name: '',
    formId: '',
  },
  FormSync
);
